import React, { useState, useRef, useEffect, useLayoutEffect } from "react";
import styled, { keyframes } from "styled-components";
import { createPortal } from "react-dom";
import {
  Building2,
  ChevronDown,
  Check,
  Plus,
  MapPin,
  Search,
  Building,
  Pencil,
  Trash2,
} from "lucide-react";
import { useAppDispatch, useAppSelector } from "../../../../Redux/Hooks";
import { getUserByToken } from "../../../../Redux/ApiCalls/Auth/login";
import { getBranchesAPI, deleteBranchAPI } from "../../../../Redux/ApiCalls/Dashboard/BranchAPI";
import { setSelectedBranch } from "../../../../Redux/Slice/Auth/AuthSlice";
import { BranchProfile } from "../../../../Data/branch.dto";
import { usePermissions } from "../../../../hooks/usePermissions";
import AddBranchModal from "../Branch/AddBranchModal";
import DeletePopup from "../../../atoms/Dashboard/Folder/DeletePopup";
import { showErrorToast, showSuccessToast } from "../../../atoms/Utlis/Toast";
import { device } from "../../../atoms/Utlis/media";

const fadeIn = keyframes`
  from {
    opacity: 0;
    transform: translateY(-6px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const SwitcherContainer = styled.div`
  position: relative;
  user-select: none;
`;

const SwitcherButton = styled.button<{ $open: boolean; $active: boolean }>`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 14px;
  border-radius: 999px;
  background: ${(p) => (p.$open ? "rgba(201, 77, 170, 0.08)" : "#fafafa")};
  border: 1px solid
    ${(p) =>
      p.$open
        ? "rgba(201, 77, 170, 0.35)"
        : p.$active
        ? "rgba(201, 77, 170, 0.25)"
        : "rgba(23, 23, 23, 0.08)"};
  color: #171717;
  cursor: pointer;
  transition: all 0.2s ease;
  font-family: Urbanist, sans-serif;
  max-width: 220px;

  &:hover {
    background: rgba(201, 77, 170, 0.06);
    border-color: rgba(201, 77, 170, 0.25);
  }

  @media ${device.tablet} {
    max-width: 170px;
    padding: 6px 10px;
  }
`;

const IconBadge = styled.div<{ $active: boolean }>`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 26px;
  height: 26px;
  border-radius: 50%;
  background: ${(p) =>
    p.$active
      ? "linear-gradient(135deg, #FFC951 0%, #C94DAA 100%)"
      : "rgba(23, 23, 23, 0.06)"};
  color: ${(p) => (p.$active ? "#ffffff" : "#737373")};
  flex-shrink: 0;
`;

const LabelWrap = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  overflow: hidden;
  text-align: left;
`;

const SubHint = styled.span`
  font-family: Montserrat, sans-serif;
  font-size: 10px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: rgba(23, 23, 23, 0.45);
  line-height: 1;
`;

const BranchTitle = styled.span`
  font-size: 13px;
  font-weight: 700;
  color: #171717;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 130px;

  @media ${device.tablet} {
    max-width: 90px;
    font-size: 12px;
  }
`;

const Chevron = styled.div<{ $open: boolean }>`
  display: flex;
  align-items: center;
  color: rgba(23, 23, 23, 0.5);
  transform: ${(p) => (p.$open ? "rotate(180deg)" : "rotate(0deg)")};
  transition: transform 0.2s ease;
  margin-left: auto;
`;

const DropdownMenu = styled.div`
  position: fixed;
  width: 320px;
  max-height: 400px;
  background: #ffffff;
  border-radius: 16px;
  border: 1px solid rgba(23, 23, 23, 0.08);
  box-shadow: 0 16px 40px rgba(0, 0, 0, 0.16);
  display: flex;
  flex-direction: column;
  overflow: hidden;
  z-index: 99999;
  animation: ${fadeIn} 0.15s ease-out;
`;

const SearchBox = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 14px;
  border-bottom: 1px solid rgba(23, 23, 23, 0.06);
  background: #fdfdfd;
`;

const SearchInput = styled.input`
  border: none;
  background: transparent;
  outline: none;
  font-family: Urbanist, sans-serif;
  font-size: 13px;
  color: #171717;
  width: 100%;

  &::placeholder {
    color: rgba(23, 23, 23, 0.4);
  }
`;

const ListScroll = styled.div`
  overflow-y: auto;
  max-height: 240px;
  padding: 6px;
`;

const OptionItem = styled.button<{ $selected: boolean }>`
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 10px 12px;
  border-radius: 10px;
  border: none;
  background: ${(p) => (p.$selected ? "rgba(201, 77, 170, 0.08)" : "transparent")};
  cursor: pointer;
  transition: all 0.15s ease;
  text-align: left;

  &:hover {
    background: ${(p) =>
      p.$selected ? "rgba(201, 77, 170, 0.12)" : "rgba(23, 23, 23, 0.04)"};
  }
`;

const OptionLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  min-width: 0;
  flex: 1;
`;

const OptionInfo = styled.div`
  display: flex;
  flex-direction: column;
  min-width: 0;
`;

const OptionName = styled.span<{ $selected: boolean }>`
  font-family: Urbanist, sans-serif;
  font-size: 13px;
  font-weight: ${(p) => (p.$selected ? "700" : "600")};
  color: ${(p) => (p.$selected ? "#c94daa" : "#171717")};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const OptionMeta = styled.span`
  font-family: Montserrat, sans-serif;
  font-size: 11px;
  color: rgba(23, 23, 23, 0.5);
  display: flex;
  align-items: center;
  gap: 4px;
`;

const OptionRight = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  flex-shrink: 0;
  margin-left: 8px;
`;

const ActionGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  opacity: 0.6;
  transition: opacity 0.15s ease;

  ${OptionItem}:hover & {
    opacity: 1;
  }
`;

const ActionBtn = styled.span<{ $danger?: boolean }>`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 22px;
  height: 22px;
  border-radius: 6px;
  border: 1px solid ${(p) => (p.$danger ? "rgba(239, 68, 68, 0.25)" : "rgba(23, 23, 23, 0.12)")};
  background: #ffffff;
  color: ${(p) => (p.$danger ? "#ef4444" : "#525252")};
  cursor: pointer;
  transition: all 0.15s ease;

  &:hover {
    border-color: ${(p) => (p.$danger ? "#ef4444" : "#c94daa")};
    color: ${(p) => (p.$danger ? "#ef4444" : "#c94daa")};
    background: ${(p) => (p.$danger ? "#fef2f2" : "rgba(201, 77, 170, 0.08)")};
  }
`;

const Divider = styled.div`
  height: 1px;
  background: rgba(23, 23, 23, 0.06);
  margin: 4px 0;
`;

const AddBranchButton = styled.button`
  width: 100%;
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px 16px;
  border: none;
  border-top: 1px solid rgba(23, 23, 23, 0.06);
  background: #fafafa;
  color: #c94daa;
  font-family: Urbanist, sans-serif;
  font-size: 13px;
  font-weight: 700;
  cursor: pointer;
  transition: background 0.15s ease;

  &:hover {
    background: rgba(201, 77, 170, 0.08);
  }
`;

type MenuCoords = {
  top?: number;
  bottom?: number;
  right: number;
};

const BranchSwitcher: React.FC = () => {
  const dispatch = useAppDispatch();
  const { branches, selectedBranch, branchesLoaded, branchesLoading, user } = useAppSelector((state) => state.auth);
  const { isStudioOwner, isSubUser, hasPermission } = usePermissions();
  const isRole4 = user?.role === 4 || (!isStudioOwner && isSubUser);
  const branchDisplayName = user?.branch_name || selectedBranch?.branch_name || "Branch";
  const canAddBranch = isStudioOwner || (!isRole4 && hasPermission("branch.create"));
  const canManageBranches = isStudioOwner || (!isRole4 && (hasPermission("branch.edit") || hasPermission("branch.create")));

  const [isOpen, setIsOpen] = useState(false);
  const [coords, setCoords] = useState<MenuCoords | null>(null);
  const [search, setSearch] = useState("");
  const [showAddModal, setShowAddModal] = useState(false);
  const [editingBranch, setEditingBranch] = useState<BranchProfile | null>(null);
  const [deletingBranch, setDeletingBranch] = useState<BranchProfile | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);

  const handleDeleteBranch = async () => {
    if (!deletingBranch) return;
    setIsDeleting(true);
    try {
      const resultAction = await dispatch(deleteBranchAPI(deletingBranch.id));
      if (deleteBranchAPI.fulfilled.match(resultAction)) {
        setDeletingBranch(null);
        dispatch(getUserByToken());
      } else {
        const errorMsg =
          typeof resultAction.payload === "string"
            ? resultAction.payload
            : (resultAction.payload as any)?.message || "Failed to delete branch";
        showErrorToast(errorMsg);
      }
    } catch (err: any) {
      showErrorToast(err?.message || "Failed to delete branch");
    } finally {
      setIsDeleting(false);
    }
  };

  useEffect(() => {
    // Initial fetch of branches if not already loaded (only for studio owners / non-role-4)
    if (!isRole4 && !branchesLoaded && !branchesLoading) {
      dispatch(getBranchesAPI());
    }
  }, [dispatch, branchesLoaded, branchesLoading, isRole4]);

  useEffect(() => {
    if (isRole4) {
      if (user?.branch_id && (!selectedBranch || selectedBranch.id !== user.branch_id)) {
        dispatch(setSelectedBranch({
          id: user.branch_id,
          branch_name: user.branch_name || "Branch",
        } as any));
      } else if (!user?.branch_id && selectedBranch) {
        dispatch(setSelectedBranch(null));
      }
    }
  }, [dispatch, isRole4, user?.branch_id, user?.branch_name, selectedBranch]);

  const updateCoords = () => {
    if (!buttonRef.current) return;
    const rect = buttonRef.current.getBoundingClientRect();
    const gap = 8;
    const menuHeight = 360;
    const spaceBelow = window.innerHeight - rect.bottom;
    const openUp = spaceBelow < menuHeight && rect.top > spaceBelow;
    const right = Math.max(8, window.innerWidth - rect.right);

    setCoords(
      openUp
        ? { bottom: window.innerHeight - rect.top + gap, right }
        : { top: rect.bottom + gap, right }
    );
  };

  useLayoutEffect(() => {
    if (!isOpen) {
      setCoords(null);
      return;
    }
    updateCoords();
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;

    window.addEventListener("resize", updateCoords);
    window.addEventListener("scroll", updateCoords, true);

    return () => {
      window.removeEventListener("resize", updateCoords);
      window.removeEventListener("scroll", updateCoords, true);
    };
  }, [isOpen]);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as Node;
      if (
        menuRef.current &&
        !menuRef.current.contains(target) &&
        buttonRef.current &&
        !buttonRef.current.contains(target)
      ) {
        setIsOpen(false);
      }
    };
    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isOpen]);

  const handleSelectBranch = async (branch: BranchProfile | null) => {
    setIsOpen(false);
    if (!branch) {
      // Switch back to "All Branches" (Studio HQ)
      dispatch(setSelectedBranch(null));
      await dispatch(getUserByToken(null));
      showSuccessToast("Switched to All Branches (Studio HQ)");
    } else {
      // Switch to specific branch
      dispatch(setSelectedBranch(branch));
      await dispatch(getUserByToken(branch.id));
      showSuccessToast(`Switched to ${branch.branch_name}`);
    }
  };

  const filteredBranches = (branches || []).filter((b) =>
    b.branch_name.toLowerCase().includes(search.toLowerCase()) ||
    (b.branch_city || b.city || "").toLowerCase().includes(search.toLowerCase())
  );

  if (isRole4) {
    return (
      <SwitcherContainer>
        <SwitcherButton
          type="button"
          as="div"
          $open={false}
          $active={true}
          style={{ cursor: "default" }}
          title={`Branch: ${branchDisplayName}`}
        >
          <IconBadge $active={true}>
            <Building2 size={15} />
          </IconBadge>
          <LabelWrap>
            <SubHint>Branch</SubHint>
            <BranchTitle title={branchDisplayName}>
              {branchDisplayName}
            </BranchTitle>
          </LabelWrap>
        </SwitcherButton>
      </SwitcherContainer>
    );
  }

  return (
    <>
      <SwitcherContainer>
        <SwitcherButton
          ref={buttonRef}
          type="button"
          onClick={() => setIsOpen((prev) => !prev)}
          $open={isOpen}
          $active={Boolean(selectedBranch)}
          title={selectedBranch ? `Active Branch: ${selectedBranch.branch_name}` : "Viewing All Branches"}
        >
          <IconBadge $active={Boolean(selectedBranch)}>
            <Building2 size={15} />
          </IconBadge>
          <LabelWrap>
            <SubHint>Branch</SubHint>
            <BranchTitle>
              {selectedBranch ? selectedBranch.branch_name : "All Branches"}
            </BranchTitle>
          </LabelWrap>
          <Chevron $open={isOpen}>
            <ChevronDown size={14} />
          </Chevron>
        </SwitcherButton>

        {isOpen &&
          coords &&
          createPortal(
            <DropdownMenu
              ref={menuRef}
              style={{
                top: coords.top,
                bottom: coords.bottom,
                right: coords.right,
              }}
              onClick={(e) => e.stopPropagation()}
            >
              {(branches?.length || 0) > 3 && (
                <SearchBox>
                  <Search size={14} color="#a3a3a3" />
                  <SearchInput
                    type="text"
                    placeholder="Search branch..."
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    autoFocus
                  />
                </SearchBox>
              )}

              <ListScroll>
                {/* Option: All Branches / HQ */}
                <OptionItem
                  type="button"
                  onClick={() => handleSelectBranch(null)}
                  $selected={selectedBranch === null}
                >
                  <OptionLeft>
                    <Building size={16} color={selectedBranch === null ? "#c94daa" : "#737373"} />
                    <OptionInfo>
                      <OptionName $selected={selectedBranch === null}>
                        All Branches
                      </OptionName>
                      <OptionMeta>Studio HQ / All records</OptionMeta>
                    </OptionInfo>
                  </OptionLeft>
                  {selectedBranch === null && <Check size={16} color="#c94daa" />}
                </OptionItem>

                {filteredBranches.length > 0 && <Divider />}

                {/* Branch Items */}
                {filteredBranches.map((b) => {
                  const isCurrent = selectedBranch?.id === b.id;
                  const displayCity = b.branch_city || b.city || "";
                  return (
                    <OptionItem
                      key={b.id}
                      type="button"
                      onClick={() => handleSelectBranch(b)}
                      $selected={isCurrent}
                    >
                      <OptionLeft>
                        <Building2 size={16} color={isCurrent ? "#c94daa" : "#737373"} />
                        <OptionInfo>
                          <OptionName $selected={isCurrent}>{b.branch_name}</OptionName>
                          <OptionMeta>
                            <MapPin size={11} /> {displayCity}{b.branch_state ? `, ${b.branch_state}` : ""}
                          </OptionMeta>
                        </OptionInfo>
                      </OptionLeft>
                      <OptionRight>
                        {isCurrent && <Check size={16} color="#c94daa" />}
                        {canManageBranches && (
                          <ActionGroup>
                            <ActionBtn
                              title="Edit branch"
                              onClick={(e) => {
                                e.stopPropagation();
                                setIsOpen(false);
                                setEditingBranch(b);
                              }}
                            >
                              <Pencil size={12} />
                            </ActionBtn>
                            <ActionBtn
                              $danger
                              title="Delete branch"
                              onClick={(e) => {
                                e.stopPropagation();
                                setIsOpen(false);
                                setDeletingBranch(b);
                              }}
                            >
                              <Trash2 size={12} />
                            </ActionBtn>
                          </ActionGroup>
                        )}
                      </OptionRight>
                    </OptionItem>
                  );
                })}

                {filteredBranches.length === 0 && search.trim() && (
                  <div style={{ padding: "14px", textAlign: "center", fontSize: "12px", color: "#737373" }}>
                    No branches found matching "{search}"
                  </div>
                )}
              </ListScroll>

              {canAddBranch && (
                <AddBranchButton
                  type="button"
                  onClick={() => {
                    setIsOpen(false);
                    setShowAddModal(true);
                  }}
                >
                  <Plus size={16} /> Add New Branch
                </AddBranchButton>
              )}
            </DropdownMenu>,
            document.body
          )}
      </SwitcherContainer>

      {showAddModal && (
        <AddBranchModal
          onClose={() => setShowAddModal(false)}
          onSuccess={(newBranch) => {
            dispatch(setSelectedBranch(newBranch));
          }}
        />
      )}

      {editingBranch && (
        <AddBranchModal
          branch={editingBranch}
          onClose={() => setEditingBranch(null)}
          onSuccess={(savedBranch) => {
            if (selectedBranch?.id === savedBranch.id) {
              dispatch(setSelectedBranch(savedBranch));
            }
            setEditingBranch(null);
          }}
        />
      )}

      {deletingBranch && (
        <DeletePopup
          text={`Are you sure you want to delete branch "${deletingBranch.branch_name}"? Records linked to this branch will have their branch unassigned.`}
          buttonText="Delete branch"
          cancel={() => setDeletingBranch(null)}
          Delete={handleDeleteBranch}
          isDeleting={isDeleting}
        />
      )}
    </>
  );
};

export default BranchSwitcher;
